import React, { useState, useEffect, useMemo } from "react";
import { Helmet } from "react-helmet";
import { createPortal } from "react-dom";
import { currentDomain, currentPath } from "../Index";
import { usePromotionsData } from "../data/PromotionData";
import Deposit from "../transactions/Deposit";
import LoginGlobal from "../data/LoginGlobal";
import { useNavigate } from "react-router-dom";
import useAppPromotionTheme from "../hooks/useAppPromotionTheme";

export default function PaydayBonus({ isLightMode }) {
	const isAppPromo = useAppPromotionTheme(isLightMode);
	const { promotionsData, isPromotionDataReady } = usePromotionsData();
	const data = useMemo(
		() => promotionsData.find((promo) => promo.title === "Payday Bonus"),
		[promotionsData],
	);
	const [isMobile, setIsMobile] = useState(false);
	const [isLoggedIn, setIsLoggedIn] = useState(false);
	const [isDepositOpen, setIsDepositOpen] = useState(false);
	const navigate = useNavigate();

	useEffect(() => {
		const loggedInState = localStorage.getItem("isLoggedIn") === "true";
		setIsLoggedIn(loggedInState);
	}, []);

	useEffect(() => {
		const handleResize = () => {
			setIsMobile(window.innerWidth < 841);
		};
		handleResize();
		window.addEventListener("resize", handleResize);
		return () => {
			window.removeEventListener("resize", handleResize);
		};
	}, []);

	useEffect(() => {
		if (!isDepositOpen) return;
		const prev = document.body.style.overflow;
		document.body.style.overflow = "hidden";
		return () => {
			document.body.style.overflow = prev;
		};
	}, [isDepositOpen]);

	const vipTiers = ["Alexandrite", "Diamond", "Ruby", "Sapphire", "Emerald"];
	// Table Data Incase
	const tableData = [
		{
			depositAmount: "500",
			bonusAMount: "50",
			vipTiers: {
				Emerald: { bonus: "✅" },
				Sapphire: { bonus: "✅" },
				Ruby: { bonus: "✅" },
				Diamond: { bonus: "✅" },
				Alexandrite: { bonus: "✅" },
			},
		},
		{
			depositAmount: "1000",
			bonusAMount: "100",
			vipTiers: {
				Emerald: { bonus: "" },
				Sapphire: { bonus: "✅" },
				Ruby: { bonus: "✅" },
				Diamond: { bonus: "✅" },
				Alexandrite: { bonus: "✅" },
			},
		},
		{
			depositAmount: "2000",
			bonusAMount: "300",
			vipTiers: {
				Emerald: { bonus: "" },
				Sapphire: { bonus: "" },
				Ruby: { bonus: "✅" },
				Diamond: { bonus: "✅" },
				Alexandrite: { bonus: "✅" },
			},
		},
		{
			depositAmount: "3000",
			bonusAMount: "600",
			vipTiers: {
				Emerald: { bonus: "" },
				Sapphire: { bonus: "" },
				Ruby: { bonus: "" },
				Diamond: { bonus: "✅" },
				Alexandrite: { bonus: "✅" },
			},
		},
		{
			depositAmount: "4000",
			bonusAMount: "750",
			vipTiers: {
				Emerald: { bonus: "" },
				Sapphire: { bonus: "" },
				Ruby: { bonus: "" },
				Diamond: { bonus: "" },
				Alexandrite: { bonus: "✅" },
			},
		},
	];

	// If the promo page is expired - throws error
	useEffect(() => {
		if (!isPromotionDataReady) return;

		if (!data) {
			navigate("/not-found");
		}
	}, [isPromotionDataReady, data, navigate]);

	if (!isPromotionDataReady || !data) return null;

	return (
		<>
			<Helmet>
				<meta charSet="utf-8" />
				<title>Payday Bonus</title>
				<link rel="icon" href="/images/fav.webp" sizes="32x32" />
				<meta name="description" content="ddd" />
				<link rel="canonical" href={`${currentDomain}${currentPath}`} />
			</Helmet>
			<div className="promotion-page-main all-page-main-container-min-height">
				<div className="promotion-page-container main-container-global-adjusted-padding">
					<div className="promotion-tnc-page">
						<div className="promotion-page-breadcrumb">
							<a
								href={
									isAppPromo
										? "/promotion/".replace("/promotion", "/app-promotion")
										: "/promotion/"
								}
							>
								Promotions{" "}
							</a>{" "}
							/ <span>{data.title}</span>
						</div>
						<div className="promotion-tnc-banner">
							<img
								src={isMobile ? data.imgSrc : data.bannerSrc}
								alt={`FGH ${data.title}`}
							/>
						</div>
						<div className="promotion-tnc-content">
							<div className="promotion-tnc-info">
								<div
									className={`promotion-indication-message ${data.promotionMessage.class}`}
								>
									{data.promotionMessage.text}
								</div>
								<h1>Payday Bonus</h1>
								<h2>Get up to ₱1,800 Bonus</h2>
								<div className="promotion-tnc-period">
									<strong>Promo Period: </strong>
									<span>Monthly Promotion</span>
								</div>
							</div>
							<div className="promotion-tnc-mechanic-content">
								<div className="table-wrapper">
									{" "}
									<table className="promotion-tnc-table">
										<thead>
											<th>Deposit Amount</th>
											<th>Bonus Amount</th>
											{vipTiers.map((tier) => (
												<th key={tier}>{tier}</th>
											))}
										</thead>
										<tbody>
											{tableData.map((row, index) => (
												<tr key={index}>
													<td>{row.depositAmount}</td>
													<td>{row.bonusAMount}</td>
													{vipTiers.map((tier) => (
														<React.Fragment key={tier}>
															<td>{row.vipTiers[tier].bonus}</td>
														</React.Fragment>
													))}
												</tr>
											))}
										</tbody>
									</table>
								</div>

								<strong>Terms & Conditions:</strong>
								<ol>
									<li>
										Promo period is every 15th (12pm) – 18th (12pm) of each
										month.
									</li>
									<li>
										Each player can only claim 1x for each deposit bonus on 1
										day, depending on the eligibility of the players based on
										their VIP Level.
									</li>
									<li>
										All deposits and bonuses from this promotion are subject to
										3x rollover requirement.
									</li>
									<li>
										We reserves the right to change, suspend or cancel the
										promotion at any time.
									</li>
									<li>
										FGH reserves the rights to take back the bonuses claimed by
										the player breaches any of the terms and conditions or if
										any foul play is detected.
									</li>
									<li>
										Select <strong className="highlight">"APPLY BONUS"</strong>{" "}
										in the Deposit or Cash In request to add the bonus to your
										deposited amount.
									</li>
									<li>
										Once applied, the bonus will be credited automatically
										together with your deposited amount.
									</li>
								</ol>
								{!isAppPromo && (
									<>
										<div className="promotion-tnc-cta">
											{isLoggedIn ? (
												<button
													type="button"
													className="promotion-cta-btn"
													style={{ cursor: "pointer" }}
													onClick={() => setIsDepositOpen(true)}
												>
													Grab your Bonus
												</button>
											) : (
												<LoginGlobal
													label="Grab your Bonus"
													btnClass="promotion-cta-btn"
													onlyBtn={true}
												/>
											)}
										</div>
										{isDepositOpen &&
											createPortal(
												<div className="modal-portal">
													<Deposit
														onClose={() => setIsDepositOpen(false)}
														defaultBonusName="New Member Package Bonus"
													/>
												</div>,
												document.body,
											)}
									</>
								)}
							</div>
						</div>
					</div>
				</div>
			</div>
		</>
	);
}
